import { LocationAlarmCamera } from '@hakimo-ui/hakimo/types';
import { Button, Checkbox } from '@hakimo-ui/shared/ui-base';
import { useMemo, useState } from 'react';
import { FIELD_CLASS } from './sop-card-utils';
import { CameraRow } from './CameraRow';

export function nameOf(camera: LocationAlarmCamera) {
  return camera.name ?? camera.id;
}

const PANEL_CLASS =
  'dark:bg-ondark-bg-1 bg-onlight-bg-1 border-onlight-line-3 dark:border-ondark-line-2 w-full space-y-2 rounded-lg border p-3 text-left shadow-xl';

interface PanelProps {
  cameras: LocationAlarmCamera[];
  value: LocationAlarmCamera[];
  onSubmit: (cameras: LocationAlarmCamera[]) => void;
  onCancel: () => void;
}

export function CameraSelectPanel(props: PanelProps) {
  const { cameras, value, onSubmit, onCancel } = props;

  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(() =>
    value.map((camera) => camera.id)
  );

  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      needle
        ? cameras.filter((camera) =>
            nameOf(camera).toLowerCase().includes(needle)
          )
        : cameras,
    [cameras, needle]
  );

  const selectable = matches.filter((camera) => camera.isTalkdownEnabled);

  const isAllSelected =
    selectable.length > 0 &&
    selectable.every((camera) => selectedIds.includes(camera.id));

  const onToggleAll = (checked: boolean) => {
    const ids = selectable.map((camera) => camera.id);
    setSelectedIds((prev) =>
      checked
        ? [...new Set([...prev, ...ids])]
        : prev.filter((id) => !ids.includes(id))
    );
  };

  const onToggle = (id: string, checked: boolean) =>
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((other) => other !== id)
    );

  return (
    <div className={PANEL_CLASS}>
      <h2 className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-bold">
        Selected Cameras
      </h2>

      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        placeholder="Select with camera name..."
        className={FIELD_CLASS}
      />

      <div className="border-onlight-line-3 dark:border-ondark-line-2 border-b pb-2">
        <Checkbox
          checked={isAllSelected}
          disabled={selectable.length === 0}
          label="Select All"
          onChange={onToggleAll}
        />
      </div>

      <div className="max-h-32 overflow-y-auto">
        {matches.map((camera) => (
          <CameraRow
            key={camera.id}
            camera={camera}
            checked={selectedIds.includes(camera.id)}
            onChange={(checked) => onToggle(camera.id, checked)}
          />
        ))}
        {matches.length === 0 && (
          <p className="text-onlight-text-3 dark:text-ondark-text-3 py-3 text-center text-xs">
            No cameras match that name.
          </p>
        )}
      </div>

      <div className="border-onlight-line-3 dark:border-ondark-line-2 flex gap-2 border-t pt-2">
        <Button
          variant="primary"
          onClick={() =>
            onSubmit(
              cameras.filter(
                (cam) => cam.isTalkdownEnabled && selectedIds.includes(cam.id)
              )
            )
          }
          disabled={selectedIds.length === 0}
        >
          Submit
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default CameraSelectPanel;
